import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'

export interface FeatureAttachment {
  id: string
  file_name: string
  file_size: number
  mime_type: string
  storage_path: string
  created_at: string
  uploaded_by: string
  message_id: string
  uploader?: {
    id: string
    full_name: string | null
    email: string
  }
}

async function fetchFeatureAttachments(featureId: string): Promise<FeatureAttachment[]> {
  // Get all attachments from messages that belong to this feature
  const { data, error } = await supabase
    .from('attachments')
    .select(`
      id,
      file_name,
      file_size,
      mime_type,
      storage_path,
      created_at,
      uploaded_by,
      message_id,
      uploader:users!uploaded_by (
        id,
        full_name,
        email
      )
    `)
    .not('message_id', 'is', null)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching attachments:', error)
    return []
  }

  // Filter to only include attachments from messages of this feature
  // We need to check which messages belong to this feature
  const { data: featureMessages, error: messagesError } = await supabase
    .from('exchange_messages')
    .select('id')
    .eq('level', 'feature')
    .eq('feature_id', featureId)

  if (messagesError) {
    console.error('Error fetching feature messages:', messagesError)
    return []
  }

  const featureMessageIds = new Set(featureMessages.map((m) => m.id))

  const filteredAttachments = data
    .filter((attachment) => attachment.message_id && featureMessageIds.has(attachment.message_id))
    .map((attachment) => ({
      ...attachment,
      // Supabase returns uploader as array, we need the first element
      uploader: Array.isArray(attachment.uploader) ? attachment.uploader[0] : attachment.uploader,
    }))

  return filteredAttachments as FeatureAttachment[]
}

export function useFeatureAttachments(featureId: string | undefined) {
  return useQuery({
    queryKey: ['featureAttachments', featureId],
    queryFn: () => fetchFeatureAttachments(featureId!),
    enabled: !!featureId,
  })
}
